/*
 * Copyright (c) 2026, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import * as v from "valibot"

import type { ApplicationTypeCategory, ApplicationTypePlatform } from "./application-type-types"

/**
 * Platforms in the order the platform menu lists them.
 *
 * `satisfies` rejects an unknown platform but not a missing one, so add a new
 * platform here by hand.
 */
export const applicationTypePlatforms = ["ANDROID", "APPLE"] as const satisfies ApplicationTypePlatform[]

/**
 * Categories in the order the category select lists them.
 *
 * As with the platforms, add a new category here by hand.
 */
export const applicationTypeCategories = ["DEV", "TEST", "PROD"] as const satisfies ApplicationTypeCategory[]

/**
 * Builds a string schema that trims the value and then rejects it when empty.
 *
 * Unlike `nonBlankString`, which keeps the spaces, the output is the trimmed
 * value. A package name can't contain spaces, and the detail page has always
 * saved descriptions trimmed.
 *
 * @param message - The error message for a blank value.
 * @returns A Valibot schema for use in `v.object`.
 */
const trimmedString = (message: string) => v.pipe(v.string(), v.trim(), v.nonEmpty(message))

/**
 * Description shared by both schemas, so creating and editing an application
 * reject the same blank description.
 */
const description = trimmedString("Enter the description")

/**
 * Icon file, optional because the user may skip it.
 *
 * It isn't sent with the other fields. The API takes the icon in a request of
 * its own.
 */
const icon = v.optional(v.file())

/**
 * Create Application dialog input.
 *
 * The output is what `createAppType` takes: the organization ID as a number and
 * the text fields trimmed.
 */
export const CreateApplicationTypeSchema = v.object({
    // A `<select>` gives a string, and the API takes the numeric ID.
    organizationId: v.pipe(v.string(), v.nonEmpty("Select an organization"), v.transform(Number)),
    sourcePackageName: trimmedString("Enter the package name"),
    description,
    icon,
    platform: v.picklist(applicationTypePlatforms),
    category: v.picklist(applicationTypeCategories)
})

/**
 * Application Detail form input.
 *
 * An admin can change only the description and the icon of an existing
 * application.
 */
export const UpdateApplicationTypeSchema = v.object({
    description,
    icon
})
